

import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react'
import * as echarts from 'echarts'
import { useUsageCounter } from './factory/useUsageCounter'
import { ITopRouteItem, ITopDataUsageItem, IUsageLog, ReportFilters } from './types'

export function UsageCounter() {
  const {
    logs,
    meta,
    topRoutes,
    topPluginspaces,
    topDataUsage,
    generalStats,
    costEstimate,
    dollarRate,
    pluginspaceStats,
    loading,
    getLogs,
    getTopRoutes,
    getTopPluginspaces,
    getTopDataUsage,
    getGeneralStats,
    getPluginspaceStats,
  } = useUsageCounter()

  // Filtros
  const [selectedMethod, setSelectedMethod] = useState<string>('')
  const [selectedRoute, setSelectedRoute] = useState<string>('')
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return d.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0]
  })
  const [topLimit, setTopLimit] = useState<number>(10)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  const days = useMemo(() => {
    if (startDate && endDate) {
      const diffMs = new Date(endDate).getTime() - new Date(startDate).getTime()
      return Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
    }
    return 7
  }, [startDate, endDate])

  const currentFilters = useMemo<ReportFilters>(() => ({
    method: selectedMethod || undefined,
    route: selectedRoute || undefined,
    createdFrom: startDate || undefined,
    createdTo: endDate || undefined,
  }), [selectedMethod, selectedRoute, startDate, endDate])

  // Refs para gráficos
  const topRoutesChartRef = useRef<HTMLDivElement>(null)
  const topPluginspacesChartRef = useRef<HTMLDivElement>(null)
  const dataUsageChartRef = useRef<HTMLDivElement>(null)

  const topRoutesChartInstance = useRef<echarts.ECharts | null>(null)
  const topPluginspacesChartInstance = useRef<echarts.ECharts | null>(null)
  const dataUsageChartInstance = useRef<echarts.ECharts | null>(null)

  const methodOptions = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH']

  const translateMethod = (method: string): string => {
    const translations: Record<string, string> = {
      GET: 'Consulta (GET)',
      POST: 'Envio (POST)',
      PUT: 'Atualização (PUT)',
      DELETE: 'Exclusão (DELETE)',
      PATCH: 'Modificação (PATCH)',
    }
    return translations[method] || method
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatUsd = (value: number): string => {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 })
  }

  const formatBrl = (value: number): string => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4 })
  }

  // Carregamento inicial
  useEffect(() => {
    if (!initialLoadComplete) {
      const loadData = async () => {
        await Promise.all([
          getLogs({ page: 1, take: 25, createdFrom: startDate, createdTo: endDate }),
          getTopRoutes(topLimit, days, currentFilters),
          getTopPluginspaces(topLimit, days, currentFilters),
          getTopDataUsage(days, topLimit, currentFilters),
          getGeneralStats(days, currentFilters),
          getPluginspaceStats(topLimit, currentFilters),
        ])
        setInitialLoadComplete(true)
      }
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Gráfico Top Rotas
  const updateTopRoutesChart = useCallback((data: ITopRouteItem[]) => {
    if (!topRoutesChartRef.current || data.length === 0) return
    if (topRoutesChartInstance.current) topRoutesChartInstance.current.dispose()

    topRoutesChartInstance.current = echarts.init(topRoutesChartRef.current)

    const routes = data.map((item) => item.route.length > 30 ? item.route.substring(0, 30) + '...' : item.route)
    const counts = data.map((item) => parseInt(item.count))

    topRoutesChartInstance.current.setOption({
      title: { text: 'Top Rotas Mais Acessadas', left: 'center', top: 10 },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const idx = params[0].dataIndex
          return `${data[idx].route}<br/>${params[0].marker} Requisições: <strong>${parseInt(data[idx].count).toLocaleString('pt-BR')}</strong>`
        },
      },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: routes, axisLabel: { rotate: 35, fontSize: 10 } },
      yAxis: { type: 'value', name: 'Requisições' },
      series: [{ name: 'Requisições', type: 'bar', data: counts, itemStyle: { color: '#5470c6' }, barMaxWidth: 40 }],
    })
  }, [])

  // Gráfico Top Pluginspaces
  const updateTopPluginspacesChart = useCallback((data: typeof topPluginspaces) => {
    if (!topPluginspacesChartRef.current || data.length === 0) return
    if (topPluginspacesChartInstance.current) topPluginspacesChartInstance.current.dispose()

    topPluginspacesChartInstance.current = echarts.init(topPluginspacesChartRef.current)

    const labels = data.map((item) => `Loja ${item.pluginspaceId}`)
    const counts = data.map((item) => parseInt(item.count))

    topPluginspacesChartInstance.current.setOption({
      title: { text: 'Top Pluginspaces (Lojas)', left: 'center', top: 10 },
      tooltip: {
        trigger: 'item',
        formatter: '{b}<br/>Requisições: <strong>{c}</strong> ({d}%)',
      },
      legend: { orient: 'vertical', left: 'left', top: 40 },
      series: [
        {
          name: 'Requisições',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['60%', '55%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
          label: { show: false, position: 'center' },
          emphasis: { label: { show: true, fontSize: 16, fontWeight: 'bold' } },
          data: labels.map((label, i) => ({ value: counts[i], name: label })),
        },
      ],
    })
  }, [])

  // Gráfico Data Usage
  const updateDataUsageChart = useCallback((data: ITopDataUsageItem[]) => {
    if (!dataUsageChartRef.current || data.length === 0) return
    if (dataUsageChartInstance.current) dataUsageChartInstance.current.dispose()

    dataUsageChartInstance.current = echarts.init(dataUsageChartRef.current)

    const dates = data.map((item) => item.date)
    const usage = data.map((item) => parseFloat(item.totalData))

    dataUsageChartInstance.current.setOption({
      title: { text: 'Consumo de Dados por Dia', left: 'center', top: 10 },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const val = params[0].value
          return `${params[0].axisValue}<br/>${params[0].marker} Dados: <strong>${formatBytes(val)}</strong>`
        },
      },
      grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
      xAxis: { type: 'category', data: dates, axisLabel: { rotate: 25 } },
      yAxis: {
        type: 'value',
        name: 'Bytes',
        axisLabel: {
          formatter: (val: number) => formatBytes(val),
        },
      },
      series: [
        {
          name: 'Dados',
          type: 'line',
          data: usage,
          smooth: true,
          areaStyle: { opacity: 0.3 },
          itemStyle: { color: '#91cc75' },
        },
      ],
    })
  }, [])

  // Atualizar gráficos quando dados mudam
  useEffect(() => {
    if (topRoutes.length > 0) updateTopRoutesChart(topRoutes)
  }, [topRoutes, updateTopRoutesChart])

  useEffect(() => {
    if (topPluginspaces.length > 0) updateTopPluginspacesChart(topPluginspaces)
  }, [topPluginspaces, updateTopPluginspacesChart])

  useEffect(() => {
    if (topDataUsage.length > 0) updateDataUsageChart(topDataUsage)
  }, [topDataUsage, updateDataUsageChart])

  // Resize dos gráficos
  useEffect(() => {
    const handleResize = () => {
      topRoutesChartInstance.current?.resize()
      topPluginspacesChartInstance.current?.resize()
      dataUsageChartInstance.current?.resize()
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Aplicar filtros na tabela de logs
  const applyFilters = async () => {
    setIsApplyingFilters(true)
    setCurrentPage(1)
    const filters: ReportFilters = {
      method: selectedMethod || undefined,
      route: selectedRoute || undefined,
      createdFrom: startDate || undefined,
      createdTo: endDate || undefined,
    }
    await Promise.all([
      getLogs({
        page: 1,
        take: 25,
        ...filters,
      }),
      getTopRoutes(topLimit, days, filters),
      getTopPluginspaces(topLimit, days, filters),
      getTopDataUsage(days, topLimit, filters),
      getGeneralStats(days, filters),
      getPluginspaceStats(topLimit, filters),
    ])
    setIsApplyingFilters(false)
  }

  const clearFilters = async () => {
    setSelectedMethod('')
    setSelectedRoute('')
    setTopLimit(10)
    const now = new Date()
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(now.getDate() - 7)
    const newStartDate = sevenDaysAgo.toISOString().split('T')[0]
    const newEndDate = now.toISOString().split('T')[0]
    setStartDate(newStartDate)
    setEndDate(newEndDate)
    setCurrentPage(1)
    const defaultFilters: ReportFilters = {
      createdFrom: newStartDate,
      createdTo: newEndDate,
    }
    await Promise.all([
      getLogs({ page: 1, take: 25, createdFrom: newStartDate, createdTo: newEndDate }),
      getTopRoutes(10, 7, defaultFilters),
      getTopPluginspaces(10, 7, defaultFilters),
      getTopDataUsage(7, 10, defaultFilters),
      getGeneralStats(7, defaultFilters),
      getPluginspaceStats(10, defaultFilters),
    ])
  }

  const handlePageChange = async (page: number) => {
    setCurrentPage(page)
    await getLogs({
      page,
      take: 25,
      method: selectedMethod || undefined,
      route: selectedRoute || undefined,
      createdFrom: startDate || undefined,
      createdTo: endDate || undefined,
    })
  }

  return (
    <div className='container-fluid'>
      {/* Header */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-body'>
              <h1 className='card-title mb-0'>
                <i className='bi bi-activity me-2'></i>
                Dashboard de Uso da API
              </h1>
              <p className='card-text text-muted'>
                Análise completa de requisições, rotas e consumo de dados
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Resumo (General Stats) */}
      <div className='row mb-4'>
        <div className='col-md-3'>
          <div className='card bg-primary text-white h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='flex-grow-1'>
                <h6 className='card-title mb-1'>Total de Requisições</h6>
                <h3 className='mb-0'>
                  {generalStats
                    ? parseInt(generalStats.totalRequests).toLocaleString('pt-BR')
                    : '—'}
                </h3>
              </div>
              <div className='ms-3'>
                <i className='bi bi-arrow-left-right fs-1'></i>
              </div>
            </div>
          </div>
        </div>

        <div className='col-md-3'>
          <div className='card bg-success text-white h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='flex-grow-1'>
                <h6 className='card-title mb-1'>Dados Enviados</h6>
                <h3 className='mb-0'>
                  {generalStats ? formatBytes(parseInt(generalStats.totalRequestSize)) : '—'}
                </h3>
              </div>
              <div className='ms-3'>
                <i className='bi bi-cloud-upload fs-1'></i>
              </div>
            </div>
          </div>
        </div>

        <div className='col-md-3'>
          <div className='card bg-warning text-white h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='flex-grow-1'>
                <h6 className='card-title mb-1'>Dados Recebidos</h6>
                <h3 className='mb-0'>
                  {generalStats ? formatBytes(parseInt(generalStats.totalResponseSize)) : '—'}
                </h3>
              </div>
              <div className='ms-3'>
                <i className='bi bi-cloud-download fs-1'></i>
              </div>
            </div>
          </div>
        </div>

        <div className='col-md-3'>
          <div className='card bg-info text-white h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='flex-grow-1'>
                <h6 className='card-title mb-1'>Tempo Médio (ms)</h6>
                <h3 className='mb-0'>
                  {generalStats
                    ? parseFloat(generalStats.avgDurationMs).toFixed(1)
                    : '—'}
                </h3>
              </div>
              <div className='ms-3'>
                <i className='bi bi-speedometer2 fs-1'></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estimativa de Custos AWS */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header d-flex justify-content-between align-items-center'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-currency-dollar me-2'></i>
                Estimativa de Custos AWS
              </h5>
              {dollarRate && (
                <small className='text-muted'>
                  Cotação USD/BRL: R$ {dollarRate.toFixed(2)}
                </small>
              )}
            </div>
            <div className='card-body'>
              {costEstimate ? (
                <div className='row g-3'>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-light h-100'>
                      <div className='card-body text-center'>
                        <i className='bi bi-hdd-network text-primary fs-3 mb-2 d-block'></i>
                        <small className='text-muted d-block'>API Gateway</small>
                        <strong className='d-block'>{formatUsd(costEstimate.apiGatewayCostUsd)}</strong>
                        <small className='text-muted'>{formatBrl(costEstimate.apiGatewayCostUsd * costEstimate.dollarRate)}</small>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-light h-100'>
                      <div className='card-body text-center'>
                        <i className='bi bi-cloud-arrow-up text-success fs-3 mb-2 d-block'></i>
                        <small className='text-muted d-block'>Transferência (Saída)</small>
                        <strong className='d-block'>{formatUsd(costEstimate.dataTransferOutCostUsd)}</strong>
                        <small className='text-muted'>{formatBrl(costEstimate.dataTransferOutCostUsd * costEstimate.dollarRate)}</small>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-light h-100'>
                      <div className='card-body text-center'>
                        <i className='bi bi-cloud-arrow-down text-warning fs-3 mb-2 d-block'></i>
                        <small className='text-muted d-block'>Transferência (Entrada)</small>
                        <strong className='d-block'>{formatUsd(costEstimate.dataTransferInCostUsd)}</strong>
                        <small className='text-muted'>Gratuito</small>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-dark text-white h-100'>
                      <div className='card-body text-center'>
                        <i className='bi bi-cash-stack fs-3 mb-2 d-block'></i>
                        <small className='d-block opacity-75'>Custo Total Estimado</small>
                        <strong className='d-block fs-5'>{formatUsd(costEstimate.totalCostUsd)}</strong>
                        <strong className='d-block fs-6 text-warning'>{formatBrl(costEstimate.totalCostBrl)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                  <div className='text-center py-3'>
                    <div className='spinner-border spinner-border-sm text-muted me-2' role='status'></div>
                    <small className='text-muted'>Calculando custos...</small>
                  </div>
                )}
              <div className='mt-3'>
                <small className='text-muted'>
                  <i className='bi bi-info-circle me-1'></i>
                  Valores estimados com base nos preços AWS API Gateway (REST API: $3.50/1M requests) e Data Transfer Out ($0.09/GB). Região us-east-1.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtro de período para gráficos */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-funnel me-2'></i>
                Filtros
              </h5>
            </div>
            <div className='card-body'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <label className='form-label'>Data Inicial</label>
                  <input
                    type='date'
                    className='form-control'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className='col-md-3'>
                  <label className='form-label'>Data Final</label>
                  <input
                    type='date'
                    className='form-control'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className='col-md-3'>
                  <label className='form-label'>Método</label>
                  <select
                    className='form-select'
                    value={selectedMethod}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                  >
                    <option value=''>Todos</option>
                    {methodOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-md-3'>
                  <label className='form-label'>Top (limite)</label>
                  <select
                    className='form-select'
                    value={topLimit}
                    onChange={(e) => setTopLimit(parseInt(e.target.value))}
                  >
                    <option value={10}>Top 10</option>
                    <option value={15}>Top 15</option>
                    <option value={20}>Top 20</option>
                    <option value={25}>Top 25</option>
                  </select>
                </div>
              </div>
              <div className='row g-3 mt-1  justify-content-end'>
                <div className='col-md-4'>
                  <label className='form-label'>&nbsp;</label>
                  <div className='d-flex gap-2 justify-content-end'>
                    <button
                      type='button'
                      className='btn btn-primary'
                      onClick={applyFilters}
                      disabled={isApplyingFilters}
                    >
                      {isApplyingFilters ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' role='status'></span>
                          Aplicando...
                        </>
                      ) : (
                          <>
                            <i className='bi bi-search me-1'></i>
                          Filtrar
                        </>
                        )}
                    </button>
                    <button
                      type='button'
                      className='btn btn-warning'
                      onClick={clearFilters}
                      disabled={isApplyingFilters}
                    >
                      <i className='bi bi-eraser me-1'></i>
                      Limpar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos - Linha 1 */}
      <div className='row mb-4'>
        {/* Top Rotas */}
        <div className='col-lg-6'>
          <div className='card h-100'>
            <div className='card-header'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-signpost-2 me-2'></i>
                Top Rotas ({startDate} a {endDate})
              </h5>
            </div>
            <div className='card-body'>
              {loading && topRoutes.length === 0 ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '350px' }}>
                  <div className='text-center'>
                    <div className='spinner-border text-primary mb-3' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                    <p className='text-muted'>Carregando rotas...</p>
                  </div>
                </div>
              ) : topRoutes.length === 0 ? (
                <div className='text-center py-5'>
                  <i className='bi bi-bar-chart display-4 text-muted'></i>
                  <p className='text-muted mt-2'>Nenhum dado disponível</p>
                </div>
              ) : (
                    <div ref={topRoutesChartRef} style={{ width: '100%', height: '350px' }}></div>
                  )}
            </div>
          </div>
        </div>

        {/* Top Pluginspaces */}
        <div className='col-lg-6'>
          <div className='card h-100'>
            <div className='card-header'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-shop me-2'></i>
                Top Pluginspaces ({startDate} a {endDate})
              </h5>
            </div>
            <div className='card-body'>
              {loading && topPluginspaces.length === 0 ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '350px' }}>
                  <div className='text-center'>
                    <div className='spinner-border text-info mb-3' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                    <p className='text-muted'>Carregando pluginspaces...</p>
                  </div>
                </div>
              ) : topPluginspaces.length === 0 ? (
                <div className='text-center py-5'>
                  <i className='bi bi-pie-chart display-4 text-muted'></i>
                  <p className='text-muted mt-2'>Nenhum dado disponível</p>
                </div>
              ) : (
                    <div ref={topPluginspacesChartRef} style={{ width: '100%', height: '350px' }}></div>
                  )}
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico Consumo de Dados */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-graph-up me-2'></i>
                Consumo de Dados ({startDate} a {endDate})
              </h5>
            </div>
            <div className='card-body'>
              {loading && topDataUsage.length === 0 ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '400px' }}>
                  <div className='text-center'>
                    <div className='spinner-border text-success mb-3' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                    <p className='text-muted'>Carregando consumo de dados...</p>
                  </div>
                </div>
              ) : topDataUsage.length === 0 ? (
                <div className='text-center py-5'>
                  <i className='bi bi-graph-up display-4 text-muted'></i>
                  <p className='text-muted mt-2'>Nenhum dado disponível</p>
                </div>
              ) : (
                    <div ref={dataUsageChartRef} style={{ width: '100%', height: '400px' }}></div>
                  )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabela Top Rotas */}
      <div className='row mb-4'>
        <div className='col-lg-6'>
          <div className='card h-100'>
            <div className='card-header'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-list-ol me-2'></i>
                Top Rotas - Detalhamento
              </h5>
            </div>
            <div className='card-body'>
              {topRoutes.length > 0 ? (
                <div className='table-responsive'>
                  <table className='table table-hover align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th scope='col'>#</th>
                        <th scope='col'>Rota</th>
                        <th scope='col' className='text-end'>
                          Requisições
                        </th>
                        <th scope='col' className='text-center'>
                          % do Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRoutes.map((item, index) => {
                        const totalCount = topRoutes.reduce(
                          (sum, r) => sum + parseInt(r.count),
                          0
                        )
                        const pct = (parseInt(item.count) / totalCount) * 100
                        return (
                          <tr key={item.route}>
                            <td>
                              <span
                                className={`badge bg-${index < 3 ? 'primary' : 'secondary'
                                  }`}
                              >
                                {index + 1}
                              </span>
                            </td>
                            <td>
                              <code className='small'>{item.route}</code>
                            </td>
                            <td className='text-end'>
                              <strong>{parseInt(item.count).toLocaleString('pt-BR')}</strong>
                            </td>
                            <td className='text-center'>
                              <div className='d-flex align-items-center justify-content-center'>
                                <div className='progress me-2' style={{ width: '60px', height: '8px' }}>
                                  <div
                                    className='progress-bar bg-primary'
                                    style={{ width: `${pct}%` }}
                                  ></div>
                                </div>
                                <small className='text-muted'>{pct.toFixed(1)}%</small>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                  <div className='text-center py-4'>
                    <i className='bi bi-table display-4 text-muted'></i>
                    <p className='text-muted mt-2'>Nenhum dado disponível</p>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Tabela Top Pluginspaces */}
        <div className='col-lg-6'>
          <div className='card h-100'>
            <div className='card-header'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-shop-window me-2'></i>
                Top Pluginspaces - Detalhamento
              </h5>
            </div>
            <div className='card-body'>
              {topPluginspaces.length > 0 ? (
                <div className='table-responsive'>
                  <table className='table table-hover align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th scope='col'>#</th>
                        <th scope='col'>Pluginspace ID</th>
                        <th scope='col' className='text-end'>
                          Requisições
                        </th>
                        <th scope='col' className='text-center'>
                          % do Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPluginspaces.map((item, index) => {
                        const totalCount = topPluginspaces.reduce(
                          (sum, p) => sum + parseInt(p.count),
                          0
                        )
                        const pct = (parseInt(item.count) / totalCount) * 100
                        return (
                          <tr key={item.pluginspaceId}>
                            <td>
                              <span
                                className={`badge bg-${index < 3 ? 'warning text-dark' : 'secondary'
                                  }`}
                              >
                                {index + 1}
                              </span>
                            </td>
                            <td>
                              <strong>Loja {item.pluginspaceId}</strong>
                            </td>
                            <td className='text-end'>
                              <strong>{parseInt(item.count).toLocaleString('pt-BR')}</strong>
                            </td>
                            <td className='text-center'>
                              <div className='d-flex align-items-center justify-content-center'>
                                <div className='progress me-2' style={{ width: '60px', height: '8px' }}>
                                  <div
                                    className='progress-bar bg-warning'
                                    style={{ width: `${pct}%` }}
                                  ></div>
                                </div>
                                <small className='text-muted'>{pct.toFixed(1)}%</small>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                  <div className='text-center py-4'>
                    <i className='bi bi-table display-4 text-muted'></i>
                    <p className='text-muted mt-2'>Nenhum dado disponível</p>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabela Custo por Pluginspace */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header d-flex justify-content-between align-items-center'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-currency-exchange me-2'></i>
                Custo Estimado por Pluginspace
              </h5>
              {dollarRate && (
                <small className='text-muted'>
                  USD/BRL: R$ {dollarRate.toFixed(2)}
                </small>
              )}
            </div>
            <div className='card-body'>
              {loading && pluginspaceStats.length === 0 ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '150px' }}>
                  <div className='text-center'>
                    <div className='spinner-border text-primary mb-3' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                    <p className='text-muted'>Carregando dados...</p>
                  </div>
                </div>
              ) : pluginspaceStats.length > 0 ? (
                <>
                  <div className='table-responsive'>
                    <table className='table table-hover align-middle'>
                      <thead className='table-light'>
                        <tr>
                          <th scope='col'>#</th>
                          <th scope='col'>Pluginspace</th>
                          <th scope='col' className='text-end'>Requisições</th>
                          <th scope='col' className='text-end'>Dados Enviados</th>
                          <th scope='col' className='text-end'>Dados Recebidos</th>
                          <th scope='col' className='text-end'>Tempo Médio</th>
                          <th scope='col' className='text-end'>API Gateway (USD)</th>
                          <th scope='col' className='text-end'>Transfer Out (USD)</th>
                          <th scope='col' className='text-end'>Total (USD)</th>
                          <th scope='col' className='text-end'>Total (BRL)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pluginspaceStats.map((item, index) => {
                          const requests = parseInt(item.totalRequests)
                          const reqSize = parseInt(item.totalRequestSize)
                          const resSize = parseInt(item.totalResponseSize)
                          const avgMs = parseFloat(item.avgDurationMs)

                          const apiGwCost = (requests / 1_000_000) * 3.5
                          const transferOutCost = (resSize / (1024 * 1024 * 1024)) * 0.09
                          const totalUsd = apiGwCost + transferOutCost
                          const totalBrl = dollarRate ? totalUsd * dollarRate : 0

                          return (
                            <tr key={item.pluginspaceId}>
                              <td>
                                <span className={`badge bg-${index < 3 ? 'primary' : 'secondary'}`}>
                                  {index + 1}
                                </span>
                              </td>
                              <td>
                                <strong>Loja {item.pluginspaceId}</strong>
                              </td>
                              <td className='text-end'>
                                {requests.toLocaleString('pt-BR')}
                              </td>
                              <td className='text-end'>
                                {formatBytes(reqSize)}
                              </td>
                              <td className='text-end'>
                                {formatBytes(resSize)}
                              </td>
                              <td className='text-end'>
                                {avgMs ? `${avgMs.toFixed(1)} ms` : '—'}
                              </td>
                              <td className='text-end'>
                                {formatUsd(apiGwCost)}
                              </td>
                              <td className='text-end'>
                                {formatUsd(transferOutCost)}
                              </td>
                              <td className='text-end'>
                                <strong>{formatUsd(totalUsd)}</strong>
                              </td>
                              <td className='text-end'>
                                <strong className='text-success'>{dollarRate ? formatBrl(totalBrl) : '—'}</strong>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot className='table-dark'>
                        <tr>
                          <td colSpan={2}><strong>Total</strong></td>
                          <td className='text-end'>
                            <strong>
                              {pluginspaceStats.reduce((s, i) => s + parseInt(i.totalRequests), 0).toLocaleString('pt-BR')}
                            </strong>
                          </td>
                          <td className='text-end'>
                            <strong>
                              {formatBytes(pluginspaceStats.reduce((s, i) => s + parseInt(i.totalRequestSize), 0))}
                            </strong>
                          </td>
                          <td className='text-end'>
                            <strong>
                              {formatBytes(pluginspaceStats.reduce((s, i) => s + parseInt(i.totalResponseSize), 0))}
                            </strong>
                          </td>
                          <td className='text-end'>—</td>
                          {(() => {
                            const totReq = pluginspaceStats.reduce((s, i) => s + parseInt(i.totalRequests), 0)
                            const totRes = pluginspaceStats.reduce((s, i) => s + parseInt(i.totalResponseSize), 0)
                            const totApiGw = (totReq / 1_000_000) * 3.5
                            const totTransfer = (totRes / (1024 * 1024 * 1024)) * 0.09
                            const totUsd = totApiGw + totTransfer
                            const totBrl = dollarRate ? totUsd * dollarRate : 0
                            return (
                              <>
                                <td className='text-end'><strong>{formatUsd(totApiGw)}</strong></td>
                                <td className='text-end'><strong>{formatUsd(totTransfer)}</strong></td>
                                <td className='text-end'><strong>{formatUsd(totUsd)}</strong></td>
                                <td className='text-end'><strong className='text-warning'>{dollarRate ? formatBrl(totBrl) : '—'}</strong></td>
                              </>
                            )
                          })()}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className='mt-2'>
                    <small className='text-muted'>
                      <i className='bi bi-info-circle me-1'></i>
                      API Gateway: $3.50/1M requests | Data Transfer Out: $0.09/GB | Região us-east-1
                    </small>
                  </div>
                </>
              ) : (
                <div className='text-center py-4'>
                  <i className='bi bi-table display-4 text-muted'></i>
                  <p className='text-muted mt-2'>Nenhum dado disponível</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Logs Paginada */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header d-flex justify-content-between align-items-center'>
              <h5 className='card-title mb-0'>
                <i className='bi bi-journal-text me-2'></i>
                Logs de Requisições
              </h5>
              {meta && (
                <small className='text-muted'>
                  {meta.itemCount.toLocaleString('pt-BR')} registros encontrados
                </small>
              )}
            </div>
            <div className='card-body'>
              {loading && logs.length === 0 ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                  <div className='text-center'>
                    <div className='spinner-border text-primary mb-3' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                    <p className='text-muted'>Carregando logs...</p>
                  </div>
                </div>
              ) : logs.length > 0 ? (
                <>
                  <div className='table-responsive'>
                    <table className='table table-hover align-middle'>
                      <thead className='table-light'>
                        <tr>
                          <th scope='col'>ID</th>
                          <th scope='col'>Método</th>
                          <th scope='col'>Rota</th>
                          <th scope='col' className='text-center'>
                            Pluginspace
                          </th>
                          <th scope='col' className='text-end'>
                            Request
                          </th>
                          <th scope='col' className='text-end'>
                            Response
                          </th>
                          <th scope='col' className='text-end'>
                            Duração
                          </th>
                          <th scope='col'>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log: IUsageLog) => (
                          <tr key={log.id}>
                            <td>
                              <small className='text-muted'>#{log.id}</small>
                            </td>
                            <td>
                              <span
                                className={`badge bg-${log.method === 'GET'
                                    ? 'success'
                                    : log.method === 'POST'
                                      ? 'primary'
                                      : log.method === 'PUT'
                                        ? 'warning text-dark'
                                        : log.method === 'DELETE'
                                          ? 'danger'
                                          : 'secondary'
                                  }`}
                              >
                                {log.method}
                              </span>
                            </td>
                            <td>
                              <code className='small'>{log.route}</code>
                            </td>
                            <td className='text-center'>
                              <span className='badge bg-light text-dark'>{log.pluginspaceId}</span>
                            </td>
                            <td className='text-end'>
                              <small>{formatBytes(log.requestSize)}</small>
                            </td>
                            <td className='text-end'>
                              <small>{formatBytes(log.responseSize)}</small>
                            </td>
                            <td className='text-end'>
                              <small>
                                {log.durationMs !== null ? `${log.durationMs} ms` : '—'}
                              </small>
                            </td>
                            <td>
                              <small className='text-muted'>
                                {new Date(log.created).toLocaleString('pt-BR')}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {meta && meta.pageCount > 1 && (
                    <nav className='mt-3'>
                      <ul className='pagination justify-content-center mb-0'>
                        <li className={`page-item ${!meta.hasPreviousPage ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={!meta.hasPreviousPage}
                          >
                            Anterior
                          </button>
                        </li>

                        {Array.from({ length: Math.min(5, meta.pageCount) }, (_, i) => {
                          let pageNum: number
                          if (meta.pageCount <= 5) {
                            pageNum = i + 1
                          } else if (currentPage <= 3) {
                            pageNum = i + 1
                          } else if (currentPage >= meta.pageCount - 2) {
                            pageNum = meta.pageCount - 4 + i
                          } else {
                            pageNum = currentPage - 2 + i
                          }
                          return (
                            <li
                              key={pageNum}
                              className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                            >
                              <button className='page-link' onClick={() => handlePageChange(pageNum)}>
                                {pageNum}
                              </button>
                            </li>
                          )
                        })}

                        <li className={`page-item ${!meta.hasNextPage ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!meta.hasNextPage}
                          >
                            Próxima
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              ) : (
                    <div className='text-center py-4'>
                      <i className='bi bi-journal-x display-4 text-muted'></i>
                      <p className='text-muted mt-2'>Nenhum log encontrado</p>
                    </div>
                  )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}